<h1 id="main-heading" class="sr-only" tabindex="-1" aria-hidden="false">Resource Routing Rules</h1>
<div class="fix-height flex justify-between">
  <div class="flex items-baseline">
    <div class="pr-4 mr-4 border-r">
      <p-button
        type="button"
        label="Add New Record"
        icon="pi pi-plus"
        [disabled]="isLoading || !access.create"
        (click)="addNewRule()"
      />
    </div>
    <div>
      <p-button
        class="mr-2"
        type="button"
        label="Save Changes"
        icon="pi pi-check"
        [disabled]="!anyDirty || isLoading || isSaving"
        [loading]="isSaving"
        (click)="saveChanges()"
      />
      <p-button
        type="button"
        label="Discard Changes"
        severity="secondary"
        icon="pi pi-times"
        [disabled]="!anyDirty || isLoading || isSaving"
        (click)="cancelChanges()"
      />
    </div>
  </div>
  <app-table-action-buttons
    [isLoading]="isLoading"
    (clearFilters)="clearFilters()"
    (fetchTableData)="fetchTableData()"
  ></app-table-action-buttons>
</div>
<form class="form-rules" [formGroup]="rulesForm">
  <div class="flex flex-col h-full" formArrayName="rules">
    <p-table
      class="block max-h-full"
      tableStyleClass="frozen-table"
      scrollHeight="flex"
      currentPageReportTemplate="Showing {first} - {last} of {totalRecords}"
      [showCurrentPageReport]="true"
      [stateKey]="tableSettings.stateKey"
      [dataKey]="tableSettings.dataKey"
      [rows]="tableSettings.rowsPerPage"
      [totalRecords]="totalRecords"
      [scrollable]="true"
      [lazy]="true"
      [paginator]="true"
      [rowHover]="true"
      [rowsPerPageOptions]="[5, 10, 20]"
      [value]="rules.controls"
      [columns]="columnConfig"
      [loading]="isLoading"
      [filterDelay]="1200"
      [lazyLoadOnInit]="false"
      [(first)]="first"
      (onLazyLoad)="fetchTableData($event)"
    >
      <ng-template #header>
        <tr>
          @for (column of columnConfig; track column) {
            <th
              pFrozenColumn
              alignFrozen="left"
              [frozen]="column.frozen || false"
              [style]="column.headerStyles"
              [pSortableColumn]="column.sortBy"
              [pSortableColumnDisabled]="!column.isSortable"
            >
              <div class="flex items-center">
                {{ column.name }}
                @if (column.isSortable) {
                  <p-sortIcon class="p-2" [field]="column.sortBy" />
                }
                @if (column.headerInfo) {
                  <span class="header-icon" [title]="column.headerInfo">
                <i class="pi pi-info-circle"></i>
              </span>
                }
              </div>
            </th>
          }
        </tr>
        <tr class="header-filter">
          @for (config of columnConfig; track config) {
            <th pFrozenColumn alignFrozen="left" [frozen]="config.frozen || false">
              <div class="truncate-child">
                @if (config.filterOptions?.template) {
                  <ng-container
                    [ngTemplateOutlet]="filterTemplates[config.filterOptions!.template!]"
                    [ngTemplateOutletContext]="{ $implicit: config }"
                  />
                }
              </div>
            </th>
          }
        </tr>
      </ng-template>
      <ng-template let-rule let-rowIndex="rowIndex" #body>
        <tr [formGroupName]="getRowIndex(rowIndex)" [class.is-inactive]="rule.value.userWarnings.error">
          @for (column of columnConfig; track column) {
            <td
              pFrozenColumn
              alignFrozen="left"
              [frozen]="column.frozen || false"
              [pEditableColumn]="rule.value[column.dataKey]"
              [pEditableColumnField]="column.dataKey"
              [pEditableColumnDisabled]="!access.update"
              [class]="{
                'p-0!': column.dataKey === 'delete',
                'is-invalid': isControlInvalid(rules?.controls[getRowIndex(rowIndex)]?.['controls']?.[column.dataKey], rule, column.dataKey)
              }"
            >
              @switch (column.dataKey) {
                @case ('user') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-select
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        formControlName="user"
                        optionLabel="name"
                        filterPlaceholder="Search"
                        dataKey="userId"
                        tooltipEvent="focus"
                        tooltipPosition="bottom"
                        tooltipStyleClass="tooltip-error"
                        [tooltip]="rule.controls['user'].invalid && tooltipError"
                        [required]="true"
                        [filter]="true"
                        [options]="users"
                      />
                      <ng-template #tooltipError>
                        <div class="flex items-center tooltip-error-template">
                          <div class="floating-error">
                            <i class="pi pi-exclamation-triangle align-middle mr-1"></i>
                            <span>The User field is required</span>
                          </div>
                        </div>
                      </ng-template>
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="userOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('secondLevelReview') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-select
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        [autoDisplayFirst]="false"
                        [options]="filtersData[column.filterOptions.propName]"
                        [formControlName]="column.dataKey"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="simpleOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('requestTypes') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        [formControlName]="column.dataKey"
                        [options]="references[column.filterOptions.propName]"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('npIs') {
                  <p-cellEditor class="truncate-child relative">
                    <ng-template #input>
                      <p-multiSelect
                        #npi
                        class="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search (min 5 digits)"
                        panelStyleClass="npi-multiselect"
                        [formControlName]="column.dataKey"
                        [options]="editNPIOptions"
                        (onFocus)="setNPIOptions(rule)"
                        (onPanelShow)="initNPISearch(rule)"
                        (onPanelHide)="editNPIOptions = []"
                        (onChange)="onNPISelect($event)"
                        (onFilter)="onNpiFilter($event)"
                      >
                        <ng-template let-option #item>
                          <div class="flex">
                            <div>{{ option }}</div>
                            @if (isInvalidNpi(option, rule)) {
                              <div
                                class="flex invalid-npi items-center ml-2"
                                title="NPI not found in the selected Client's network."
                              >
                                <i class="pi pi-exclamation-triangle"></i>
                              </div>
                            }
                          </div>
                        </ng-template>
                      </p-multiSelect>
                      @if (hasError136(rule)) {
                        <div class="floating-error">
                          <i class="pi pi-exclamation-circle align-middle mr-1"></i>
                          <span class="w-full">NPIs not found in the selected Client's network.</span>
                        </div>
                      }
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('specialty') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-select
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        optionValue="value"
                        [autoDisplayFirst]="false"
                        [options]="filtersData[column.filterOptions.propName]"
                        [formControlName]="column.dataKey"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="simpleOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('providerDSERuleGroups') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        [formControlName]="column.dataKey"
                        [options]="references[column.filterOptions.propName]"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('postalStates') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        tooltipPosition="left"
                        tooltip="Add states where the reviewer is not licensed. The reviewer will not receive the lines from Servicing Facilities in these states."
                        [tooltipDisabled]="productName !== 'pac-ma'"
                        [formControlName]="column.dataKey"
                        [options]="references[column.filterOptions.propName]"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('states') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        [formControlName]="column.dataKey"
                        [options]="references[column.referenceKey]"
                      >
                      </p-multiSelect>
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      ></ng-container>
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('orderSubtypes') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        [formControlName]="column.dataKey"
                        [options]="references[column.filterOptions.propName]"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('hcpcCodes') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        [formControlName]="column.dataKey"
                        [options]="references[column.filterOptions.propName]"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('tenants') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        panelStyleClass="client-multiselect"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        [formControlName]="column.dataKey"
                        [options]="references[column.filterOptions.propName]"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                    $implicit: rule,
                    dataKey: column.dataKey
                  }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('authorizationTypes') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        [formControlName]="column.dataKey"
                        [options]="references[column.filterOptions.propName]"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('mco') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        [formControlName]="column.dataKey"
                        [options]="references[column.referenceKey]"
                      >
                      </p-multiSelect>
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="decoratedArrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      ></ng-container>
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('categories') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-multiSelect
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        filterPlaceHolder="Search"
                        [formControlName]="column.dataKey"
                        [options]="references[column.referenceKey]"
                      >
                      </p-multiSelect>
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="arrayOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      ></ng-container>
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('tenantName') {
                  <p-cellEditor class="truncate-child">
                    <ng-template #input>
                      <p-select
                        class="w-full"
                        styleClass="w-full"
                        appendTo="body"
                        [autoDisplayFirst]="false"
                        [options]="filtersData[column.referenceKey]"
                        [formControlName]="column.dataKey"
                      />
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="simpleOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('dailyLimit') {
                  <p-cellEditor class="truncate-child relative">
                    <ng-template #input>
                      <p-inputNumber inputStyleClass="w-full" [min]="0" [formControlName]="column.dataKey" />
                      @if (rule.controls.dailyLimit.errors?.max) {
                        <div class="floating-error">
                          <i class="pi pi-exclamation-circle align-middle mr-1"></i>
                          <span class="w-full">The Daily Line Limit value must be in range 0-99999</span>
                        </div>
                      }
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="simpleOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('dailyCount') {
                  <span>{{ rule.value.user?.dailyCount }}</span>
                }
                @case ('totalLimit') {
                  <p-cellEditor class="truncate-child relative">
                    <ng-template #input>
                      <p-inputNumber inputStyleClass="w-full" [min]="0" [formControlName]="column.dataKey" />
                      @if (rule.controls['totalLimit'].invalid && (rule.controls['totalLimit'].dirty ||
                        rule.controls['totalLimit'].touched)) {
                        @if (rule.controls['totalLimit'].errors?.max) {
                          <div class="floating-error">
                            <i class="pi pi-exclamation-circle align-middle mr-1"></i>
                            <span
                            >The {{ column.name }} value must be in range 0-{{
                                rule.controls['totalLimit'].errors.max.max
                              }}</span
                            >
                          </div>
                        }
                        @if (rule.controls['totalLimit'].errors?.required) {
                          <div class="floating-error">
                            <i class="pi pi-exclamation-circle align-middle mr-1"></i>
                            <span>The {{ column.name }} value is required</span>
                          </div>
                        }
                      }
                    </ng-template>
                    <ng-template #output>
                      <ng-container
                        [ngTemplateOutlet]="simpleOutput"
                        [ngTemplateOutletContext]="{
                          $implicit: rule,
                          dataKey: column.dataKey,
                          align: productName === 'dmepos'
                        }"
                      />
                    </ng-template>
                  </p-cellEditor>
                }
                @case ('totalCount') {
                  <div [ngClass]="{ 'text-right': productName === 'dmepos' }">
                    {{ rule.value.user?.totalCount }}
                  </div>
                }
                @case ('isActive') {
                  <div class="w-full flex justify-center items-center">
                    <p-toggle-switch class="leading-none" [formControlName]="column.dataKey" />
                  </div>
                }
                @case ('delete') {
                  <div class="w-full text-center">
                    <p-button
                      type="button"
                      icon="pi pi-trash"
                      aria-label="Delete"
                      [text]="true"
                      [rounded]="true"
                      [disabled]="!access.delete"
                      (click)="deleteRule(rule, rowIndex)"
                    />
                  </div>
                }
                @default {
                  <div class="truncate-child">
                    <span class="truncate">{{ rule[column.dataKey] }}</span>
                  </div>
                }
              }
            </td>
          }
        </tr>
      </ng-template>
      <ng-template let-columns #emptymessage>
        <tr>
          <td class="w-full p-6" [attr.colspan]="columns.length + 1">No records found</td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</form>

<!-- Edit Templates -->
<ng-template #simpleOutput let-rule let-key="dataKey" let-align="align">
  <div class="truncate" [title]="rule.value[key]" [class.text-right]="align">
    {{ rule.value[key] }}
  </div>
  @if (rule.controls[key]?.dirty) {
    <i class="pi pi-pencil p-1"></i>
  }
</ng-template>

<ng-template #arrayOutput let-rule let-key="dataKey">
  <div class="truncate" [title]="rule.value[key] | join : ', '">
    {{ rule.value[key] | join : ', ' }}
  </div>
  @if (rule.controls[key]?.dirty) {
    <i class="pi pi-pencil p-1"></i>
  }
</ng-template>

<ng-template #decoratedArrayOutput let-rule let-key="dataKey">
  <ul [title]="rule.value[key] | join : '\n'">
    @for (mco of rule.value[key]; track mco) {
      <li>
        <span class="block truncate w-full">{{ mco }}</span>
      </li>
    }
  </ul>
</ng-template>

<ng-template #userOutput let-rule let-key="dataKey">
  <div class="flex justify-between items-center w-full">
    <div class="whitespace-nowrap overflow-hidden text-ellipsis" title="Rule ID: {{ rule.value[tableSettings.dataKey] }}">
      {{ rule.value.user?.name }}
    </div>
    <div class="flex gap-2">
      @if (rule.value.userWarnings.error) {
        <div class="leading-none cursor-pointer" (mouseenter)="userWarn.show($event)" (mouseleave)="userWarn.hide()">
          <i class="pi pi-exclamation-triangle icon-warning"></i>
        </div>
        <p-popover #userWarn [dt]="DarkPopover">
          {{ rule.value.userWarnings.message }}
        </p-popover>
      }
      @if (rule.controls[key]?.dirty) {
        <i class="pi pi-pencil"></i>
      }
    </div>
  </div>
</ng-template>

<!-- Header filter templates -->
<ng-template #select let-config>
  <p-select
    class="w-full"
    appendTo="body"
    placeholder="Filter"
    [showClear]="true"
    [options]="filtersData[config.filterOptions.propName]"
    [ngModel]="table.filters[config?.filterOptions?.propName]?.value"
    (onChange)="table.filter($event.value, config.filterOptions.propName, 'in')"
  />
</ng-template>

<ng-template #multiselect let-config>
  <p-multiSelect
    class="w-full"
    placeholder="Filter"
    appendTo="body"
    filterPlaceHolder="Search"
    [virtualScrollItemSize]="36"
    [resetFilterOnHide]="true"
    [selectedItemsLabel]="'{0} items selected'"
    [virtualScroll]="config.filterOptions.virtualScroll"
    [filter]="config.filterOptions.filterEnabled"
    [options]="filtersData[config.filterOptions.propName]"
    [ngModel]="table.filters[config.filterOptions.filterByProp || config.filterOptions.propName]?.value"
    (onChange)="table.filter($event.value, config.filterOptions.filterByProp || config.filterOptions.propName, 'in')"
  >
    <ng-template let-option #item>
      {{ option.label }}
      @if (getTooltipInMultiSelectOption(option, config.filterOptions.propName); as tooltip) {
        <span [title]="tooltip">
        <i class="pi pi-info-circle ml-2 items-center"></i>
      </span>
      }
    </ng-template>
  </p-multiSelect>
</ng-template>

<ng-template #user let-config>
  <p-multiSelect
    class="w-full"
    panelStyleClass="user-filter-dropdown"
    placeholder="Filter"
    appendTo="body"
    filterPlaceHolder="Search"
    [resetFilterOnHide]="true"
    [showToggleAll]="false"
    [group]="true"
    [selectedItemsLabel]="userFilterSelectedLabel"
    [options]="filtersData[config.filterOptions.propName]"
    [ngModel]="table.filters[config?.filterOptions?.propName]?.value"
    (onChange)="filterByUser($event, config.filterOptions.propName)"
  />
</ng-template>
